// Device web-push enrollment for daily reminders. iPhone and iPad deliver
// web push only to an installed Home Screen app (iOS 16.4+), so support is
// detected from real capabilities and the UI guides installation when needed.
import { supabase } from "../lib/supabase";

export const REMINDER_API =
  import.meta.env.VITE_REMINDER_API_URL ||
  "https://memsoup-reminders.memsoup.workers.dev";

const VAPID_PUBLIC_KEY =
  "BHz4iXTDn0oRs6DWNdNNi58za0pJqGnajGRCo7nxtNgIHyDZu5a35Owfjb6UynszksCG_K0kmYN7xsJ6dktTSwI";

export type PushSupport = "ready" | "needs-install" | "unsupported";

export function pushSupport(): PushSupport {
  if (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  ) {
    return "ready";
  }
  const agent = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const appleTouch =
    /iPhone|iPad|iPod/.test(agent) ||
    (agent.includes("Macintosh") && navigator.maxTouchPoints > 1);
  return appleTouch ? "needs-install" : "unsupported";
}

function applicationServerKey(): Uint8Array<ArrayBuffer> {
  const padded = VAPID_PUBLIC_KEY.replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) {
    bytes[index] = raw.charCodeAt(index);
  }
  return bytes;
}

async function readyRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
    ]);
  } catch {
    return null;
  }
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== "ready") return null;
  const registration = await readyRegistration();
  if (!registration) return null;
  try {
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

async function reminderRequest(
  method: "POST" | "DELETE",
  body: Record<string, unknown>,
): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) return false;
  try {
    const response = await fetch(`${REMINDER_API}/reminder`, {
      method,
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function saveDevicePush(
  subscription: PushSubscription,
  remindTime: string,
): Promise<boolean> {
  const details = subscription.toJSON();
  if (!details.endpoint || !details.keys?.p256dh || !details.keys?.auth) {
    return false;
  }
  return reminderRequest("POST", {
    endpoint: details.endpoint,
    p256dh: details.keys.p256dh,
    auth: details.keys.auth,
    time: remindTime,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

export type EnableOutcome = "enabled" | "denied" | "failed";

export async function enableDevicePush(
  remindTime: string,
): Promise<EnableOutcome> {
  if (pushSupport() !== "ready") return "failed";
  let permission: NotificationPermission = "denied";
  try {
    permission = await Notification.requestPermission();
  } catch {
    return "failed";
  }
  if (permission !== "granted") return "denied";
  const registration = await readyRegistration();
  if (!registration) return "failed";
  try {
    const subscription =
      (await registration.pushManager.getSubscription()) ||
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(),
      }));
    return (await saveDevicePush(subscription, remindTime))
      ? "enabled"
      : "failed";
  } catch {
    return "failed";
  }
}

export async function disableDevicePush(): Promise<void> {
  const subscription = await currentPushSubscription();
  if (!subscription) return;
  await reminderRequest("DELETE", { endpoint: subscription.endpoint });
  try {
    await subscription.unsubscribe();
  } catch {
    // The push service forgets the endpoint when the reminder row is gone.
  }
}
